import { AnyType, IPredicate, ISelector, TUserTag, withTags } from '@centra/centra-ui'
import intersection from 'lodash/intersection'
import { IUser } from 'store/authentication/model'
import {
  CategoryTypeIds,
  IUpdateFiltersByQuery,
  StatusTypeIds,
  TASK_REPORT_NG_API_SELECTOR,
} from 'store/taskManager/model'
import {
  DOCS_TYPE_PROPERTY_GID,
  PRODUCT_PROPERTY_GID,
  SUPPORT_TYPE_PROPERTY_GID,
} from '../components/SingleTask/taskAside/consts'

// Статусы тасок доступных для загрузки (Активные задачи)
const regularStatuses = [
  StatusTypeIds.briefing,
  StatusTypeIds.inProgress,
  StatusTypeIds.needInformation,
  StatusTypeIds.research,
  StatusTypeIds.onConfirmed,
]

const buildBasicTasksSelector = (): ISelector => {
  return {
    ...TASK_REPORT_NG_API_SELECTOR,
    predicates: [...TASK_REPORT_NG_API_SELECTOR.predicates] as IPredicate[],
    paging: {
      offset: 0,
      limit: 200,
    },
  }
}

const buildBasicSingleTaskSelector = (taskId: number): ISelector => {
  return {
    fields: [
      ...TASK_REPORT_NG_API_SELECTOR.fields,
      'accountManagerId',
      'accountManagerRoles',
      'accountManagerFirstName',
      'accountManagerLastName',
      'accountManagerAvatar',
      'accountManagerTitle',
      'supportUsers',
      'lastCommunicationUser',
      'finCoordinators',
      'propertyValues',
      'createdAt',
      'author',
      'isClientConfirm',
    ],
    predicates: [
      {
        field: 'id',
        operator: 'EQUALS',
        values: [taskId],
      },
    ] as IPredicate[],
    paging: {
      offset: 0,
      limit: 1,
    },
  }
}

const buildTasksFilters = (user: IUser, regularStatuses: number[], filter: IUpdateFiltersByQuery): IPredicate[] => {
  const statusFilter = [
    {
      field: 'statusId',
      operator: 'IN',
      values: filter.statusIds?.length ? intersection(regularStatuses, filter.statusIds) : regularStatuses,
    },
  ] as IPredicate[]

  if (!withTags({ tags: ['role.admin', 'role.supervisor', 'role.account-manager'], user: user as AnyType })) {
    return statusFilter
  }

  return [
    ...statusFilter,
    ...(withTags({ tags: ['role.admin', 'role.supervisor', 'role.support'], user: user as AnyType }) &&
    filter.accountManagerIds?.length
      ? [
          {
            field: 'assignedAccountId',
            operator: 'IN',
            values: filter.accountManagerIds,
          },
        ]
      : []),
    ...(filter.clientIds?.length
      ? [
          {
            //Запрашивается по полю 'clientManagerClientId', чтобы возвращались и клиенты агенств
            field: 'clientManagerClientId',
            operator: 'IN',
            values: filter.clientIds,
          },
        ]
      : []),
    ...(filter.categoryIds?.length
      ? [
          {
            field: 'categoryId',
            operator: 'IN',
            values: filter.categoryIds,
          },
        ]
      : []),
    ...(filter.supportTypeIds?.length
      ? [
          {
            field: 'propertyValuesText',
            operator: 'MATCH',
            values: [`"propertyGid": "${SUPPORT_TYPE_PROPERTY_GID}"`],
          },
          {
            field: 'propertyValuesText',
            operator: 'MATCH',
            values: [`"optionValue": \\[(${filter.supportTypeIds.join('|')})\\]`],
          },
        ]
      : []),
    ...(filter.docsTypeId?.length
      ? [
          {
            field: 'propertyValuesText',
            operator: 'MATCH',
            values: [`"propertyGid": "${DOCS_TYPE_PROPERTY_GID}"`],
          },
          {
            field: 'propertyValuesText',
            operator: 'MATCH',
            values: [`"optionValue": \\[(${filter.docsTypeId.join('|')})\\]`],
          },
        ]
      : []),
    ...(filter.productIds?.length
      ? [
          {
            field: 'propertyValuesText',
            operator: 'MATCH',
            values: [`"propertyGid": "${PRODUCT_PROPERTY_GID}"`],
          },
          {
            field: 'propertyValuesText',
            operator: 'MATCH',
            values: [`"integerValue": (${filter.productIds.join('|')})`],
          },
        ]
      : []),
    ...(filter.createdFrom?.length
      ? [
          {
            field: 'createdAt',
            operator: 'GREATER_THAN_EQUALS',
            values: filter.createdFrom,
          },
        ]
      : []),
    ...(filter.createdTo?.length
      ? [
          {
            field: 'createdAt',
            operator: 'LESS_THAN_EQUALS',
            values: filter.createdTo,
          },
        ]
      : []),
    ...(filter.taskId?.length
      ? [
          {
            field: 'summaryTaskId',
            operator: 'STARTS_WITH',
            values: filter.taskId,
          },
        ]
      : []),
  ] as IPredicate[]
}

/**
 * Сборка селектора для запроса "Срочные задачи"
 */
export const buildUrgentTasksSelector = (user: IUser, filter: IUpdateFiltersByQuery): ISelector => {
  let rolePredicates = [] as IPredicate[]
  const { id } = user

  if (withTags({ user: user as AnyType, tags: 'role.account-manager' })) {
    rolePredicates = [
      /**
       * Клиентским менеджером в задаче являются пользователи с ролями ГМА, ГМР, МА, МР
       */
      {
        field: 'isTaskClientManagerHasManagersRoles',
        operator: 'EQUALS',
        values: [true],
      },
      {
        field: 'categoryId',
        operator: 'NOT_EQUALS',
        values: [CategoryTypeIds.ai],
      },
    ]

    // Прячем список задач на подключение для специалиста ОР
    if (withTags({ user: user as AnyType, tags: 'role.growthTeamManager' as TUserTag })) {
      rolePredicates = [
        ...rolePredicates,
        {
          field: 'categoryId',
          operator: 'NOT_IN',
          values: [CategoryTypeIds.connection, CategoryTypeIds.ai],
        },
      ]
    }

    if (!withTags({ user: user as AnyType, tags: ['role.growthTeamManager', 'role.support'] as TUserTag[] })) {
      rolePredicates = [
        ...rolePredicates,
        {
          field: 'assignedAccountId',
          operator: 'EQUALS',
          values: [id],
        },
        {
          field: 'clientManagerId',
          operator: 'NOT_EQUALS',
          values: [id],
        },
      ]
    }
  } else if (withTags({ user: user as AnyType, tags: ['role.admin', 'role.supervisor'] })) {
    /**
     * Вместо /task/all
     */
    rolePredicates = [
      {
        field: 'assignedAccountId',
        operator: 'NOT_EQUALS',
        values: [id],
      },
      {
        field: 'clientManagerId',
        operator: 'NOT_EQUALS',
        values: [id],
      },
      {
        field: 'categoryId',
        operator: 'NOT_EQUALS',
        values: [CategoryTypeIds.ai],
      },
      /**
       * Клиентский менеджер задачи не должен быть ответственным
       */
      {
        field: 'isTaskClientManagerEqualAssigned',
        operator: 'EQUALS',
        values: [false],
      },
    ]
  }

  const selector = buildBasicTasksSelector()
  const filters = buildTasksFilters(user, regularStatuses, filter)

  selector.predicates = [
    ...(selector.predicates as IPredicate[]),
    ...rolePredicates,
    ...filters,
    {
      field: 'isUrgent',
      operator: 'EQUALS',
      values: [true],
    },
  ] as IPredicate[]

  return selector
}

/**
 * Сборка селектора для запроса "Задачи менеджеров / Мои задачи"
 */
export const buildAccountManagersTasksSelector = (user: IUser, filter: IUpdateFiltersByQuery): ISelector => {
  let rolePredicates = [] as IPredicate[]
  const { id } = user

  if (withTags({ user: user as AnyType, tags: 'role.account-manager' })) {
    rolePredicates = [
      /**
       * Клиентским менеджером в задаче являются пользователи с ролями ГМА, ГМР, МА, МР
       */
      {
        field: 'isTaskClientManagerHasManagersRoles',
        operator: 'EQUALS',
        values: [true],
      },
      {
        field: 'isUrgent',
        operator: 'EQUALS',
        values: [false],
      },
      {
        field: 'categoryId',
        operator: 'NOT_EQUALS',
        values: [CategoryTypeIds.ai],
      },
    ]

    // Прячем список задач на подключение для специалиста ОР
    if (withTags({ user: user as AnyType, tags: 'role.growthTeamManager' as TUserTag })) {
      rolePredicates = [
        ...rolePredicates,
        {
          field: 'categoryId',
          operator: 'NOT_IN',
          values: [CategoryTypeIds.connection, CategoryTypeIds.ai],
        },
      ]
    }

    if (!withTags({ user: user as AnyType, tags: ['role.growthTeamManager', 'role.support'] as TUserTag[] })) {
      rolePredicates = [
        ...rolePredicates,
        {
          field: 'assignedAccountId',
          operator: 'EQUALS',
          values: [id],
        },
        {
          field: 'clientManagerId',
          operator: 'NOT_EQUALS',
          values: [id],
        },
      ]
    }
  } else if (withTags({ user: user as AnyType, tags: ['role.admin', 'role.supervisor'] })) {
    /**
     * Вместо /task/all
     */
    rolePredicates = [
      {
        field: 'assignedAccountId',
        operator: 'NOT_EQUALS',
        values: [id],
      },
      {
        field: 'clientManagerId',
        operator: 'NOT_EQUALS',
        values: [id],
      },
      {
        field: 'categoryId',
        operator: 'NOT_EQUALS',
        values: [CategoryTypeIds.ai],
      },
      /**
       * Клиентский менеджер задачи не должен быть ответственным
       */
      {
        field: 'isTaskClientManagerEqualAssigned',
        operator: 'EQUALS',
        values: [false],
      },
    ]
  } else if (
    withTags({
      user: user as AnyType,
      tags: [
        'role.agency-primary-manager',
        'role.agency-manager',
        'role.client-manager',
        'role.client-primary-manager',
      ],
    })
  ) {
    /*
        isAccountManager: true,
        isDone: false,
        */
    rolePredicates = [
      {
        field: 'clientManagerId',
        operator: 'IN',
        values: [id],
      },
      {
        field: 'assignedAccountId',
        operator: 'NOT_IN',
        values: [id],
      },
      {
        field: 'statusId',
        operator: 'NOT_IN',
        values: [StatusTypeIds.done, StatusTypeIds.reject],
      },
    ]
  }

  const selector = buildBasicTasksSelector()
  const filters = buildTasksFilters(user, regularStatuses, filter)

  selector.predicates = [...(selector.predicates as IPredicate[]), ...rolePredicates, ...filters]

  return selector
}

/**
 * Сборка селектора для запроса "Задачи клиентов"
 */
export const buildClientsTasksSelector = (user: IUser, filter: IUpdateFiltersByQuery): ISelector => {
  const { id } = user
  let rolePredicates = [] as IPredicate[]

  if (withTags({ tags: 'role.account-manager', user: user as AnyType })) {
    /**
     * Вместо /task/manager/clients
     */
    rolePredicates = [
      /**
       * В качестве ответственных в задаче выступают пользователи с ролями ГМА, ГМР, МА, МР
       */
      {
        field: 'isAssignedHasManagersRoles',
        operator: 'EQUALS',
        values: [true],
      },
      {
        field: 'isUrgent',
        operator: 'EQUALS',
        values: [false],
      },
      {
        field: 'categoryId',
        operator: 'NOT_EQUALS',
        values: [CategoryTypeIds.ai],
      },
    ]

    // Прячем список задач на подключение для специалиста ОР
    if (withTags({ user: user as AnyType, tags: 'role.growthTeamManager' as TUserTag })) {
      rolePredicates = [
        ...rolePredicates,
        {
          field: 'categoryId',
          operator: 'NOT_IN',
          values: [CategoryTypeIds.connection, CategoryTypeIds.ai],
        },
      ]
    }

    if (!withTags({ user: user as AnyType, tags: ['role.growthTeamManager', 'role.support'] as TUserTag[] })) {
      rolePredicates = [
        ...rolePredicates,
        {
          field: 'assignedAccountId',
          operator: 'NOT_EQUALS',
          values: [id],
        },
        {
          field: 'clientManagerId',
          operator: 'NOT_EQUALS',
          values: [id],
        },
      ]
    }
  } else if (withTags({ tags: ['role.admin', 'role.supervisor'], user: user as AnyType })) {
    /**
     * Вместо /task/all/clients
     */
    rolePredicates = [
      {
        field: 'assignedAccountId',
        operator: 'NOT_EQUALS',
        values: [id],
      },
      {
        field: 'clientManagerId',
        operator: 'NOT_EQUALS',
        values: [id],
      },
      {
        field: 'categoryId',
        operator: 'NOT_EQUALS',
        values: [CategoryTypeIds.ai],
      },
      /**
       * Менеджер клиента в задаче является одновременно и ответственным
       */
      {
        field: 'isTaskClientManagerEqualAssigned',
        operator: 'EQUALS',
        values: [true],
      },
      {
        field: 'isUrgent',
        operator: 'EQUALS',
        values: [false],
      },
    ]
  } else if (
    withTags({
      tags: [
        'role.agency-primary-manager',
        'role.agency-manager',
        'role.client-manager',
        'role.client-primary-manager',
      ],
      user: user as AnyType,
    })
  ) {
    /*
        isAccountManager: false,
        isDone: false,
      */
    rolePredicates = [
      {
        field: 'clientManagerId',
        operator: 'IN',
        values: [id],
      },
      {
        field: 'assignedAccountId',
        operator: 'IN',
        values: [id],
      },
      {
        field: 'statusId',
        operator: 'NOT_IN',
        values: [StatusTypeIds.done, StatusTypeIds.reject],
      },
    ]
  }

  const selector = buildBasicTasksSelector()
  const filters = buildTasksFilters(user, regularStatuses, filter)

  selector.predicates = [...(selector.predicates as IPredicate[]), ...rolePredicates, ...filters]

  return selector
}

/**
 * Сборка селектора для запроса "Выполненные задачи"
 */
export const buildDoneTasksSelector = (user: IUser, filter: IUpdateFiltersByQuery): ISelector => {
  let rolePredicates = [] as IPredicate[]

  const regularStatuses = [StatusTypeIds.done, StatusTypeIds.reject]

  if (withTags({ user: user as AnyType, tags: ['role.account-manager'] })) {
    /**
     * Вместо /task/manager
     */
    rolePredicates = [
      /**
       * Клиентским менеджером в задаче являются пользователи с ролями ГМА, ГМР, МА, МР
       */
      {
        field: 'isTaskClientManagerHasManagersRoles',
        operator: 'EQUALS',
        values: [true],
      },
      {
        field: 'categoryId',
        operator: 'NOT_EQUALS',
        values: [CategoryTypeIds.ai],
      },
    ]

    // Прячем список задач на подключение для специалиста ОР
    if (withTags({ user: user as AnyType, tags: 'role.growthTeamManager' as TUserTag })) {
      rolePredicates = [
        ...rolePredicates,
        {
          field: 'categoryId',
          operator: 'NOT_IN',
          values: [CategoryTypeIds.connection, CategoryTypeIds.ai],
        },
      ]
    }
  } else if (withTags({ user: user as AnyType, tags: ['role.admin', 'role.supervisor'] })) {
    /**
     * Вместо /task/all
     *
     * У этих ролей только фильтры
     */
  } else if (
    withTags({
      user: user as AnyType,
      tags: [
        'role.agency-manager',
        'role.agency-primary-manager',
        'role.client-manager',
        'role.client-primary-manager',
      ],
    })
  ) {
    /*
        isDone: true,
      */
    rolePredicates = [
      {
        field: 'clientManagerId',
        operator: 'IN',
        values: [user.id],
      },
    ]
  }

  const selector = buildBasicTasksSelector()
  const filters = buildTasksFilters(user, regularStatuses, filter)

  selector.predicates = [...(selector.predicates as IPredicate[]), ...rolePredicates, ...filters]

  return selector
}

/**
 * Сборка селектора для запроса отдельной задачи
 */
export const buildSingleTaskSelector = (user: IUser, taskId: number): ISelector => {
  const rolePredicates = [] as IPredicate[]

  if (
    withTags({
      user: user as AnyType,
      tags: ['role.admin', 'role.supervisor', 'role.account-manager', 'role.growthTeamManager' as TUserTag],
    })
  ) {
    rolePredicates.push({
      field: 'categoryId',
      operator: 'NOT_EQUALS',
      values: [CategoryTypeIds.ai],
    })
  }

  const selector = buildBasicSingleTaskSelector(taskId)
  selector.predicates = [...(selector.predicates as IPredicate[]), ...rolePredicates]

  return selector
}
